import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { JSX, useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { BiTrash, BiSearch } from "react-icons/bi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import PopoverMenu from "@/components/ui/Popeover";
import { Edit, Eye, Trash2 } from "lucide-react";
import TextField from "@/components/ui/Textfield";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

export default function ManageAdCategories(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [categoryToRestore, setCategoryToRestore] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 25;

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const {
    resetValue,
    getAdCategoryData,
    adCategoryApiData,
    setEditData,
    getAdCategoryDetailData,
    deleteAdCategory,
    getDeletedAdCategory,
    restoreAdCategory,
  } = useAdCategoryStore(
    useShallow((state) => ({
      resetValue: state.resetValue,
      getAdCategoryData: state.getAdCategoryData,
      adCategoryApiData: state.adCategoryApiData,
      setEditData: state.setEditData,
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      deleteAdCategory: state.deleteAdCategory,
      getDeletedAdCategory: state.getDeletedAdCategory,
      restoreAdCategory: state.restoreAdCategory,
    })),
  );

  const setError = useGlobalStore((state) => state.setError);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const type = searchParams.get("type");
      if (type === "deleted") {
        await getDeletedAdCategory();
      } else {
        await getAdCategoryData([{ key: "sort", value: sortField }]);
      }
    } catch (error) {
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteAdCategory(categoryToDelete.id);
      await fetchData();
      setCategoryToDelete(null);
    } catch (error) {
      setError(error.message ?? "Failed to delete category");
    }
  };

  const handleRestore = async () => {
    if (!categoryToRestore) return;

    try {
      await restoreAdCategory(categoryToRestore.id);
      await fetchData();
      setCategoryToRestore(null);
    } catch (error) {
      setError(error.message ?? "Failed to restore category");
    }
  };

  const createMenuItems = (adCategoryId: string, categoryName: string) => [
    {
      label: "View Ad Category",
      icon: <Eye className="w-4 h-4" />,
      onClick: () => navigate(`/ad-categories/publishers?id=${adCategoryId}`),
    },
    {
      label: "Edit Ad Category",
      icon: <Edit className="w-4 h-4" />,
      onClick: async () => {
        await getAdCategoryDetailData(adCategoryId);
        const detailData = useAdCategoryStore.getState().adCategoryDetailData;
        if (detailData) {
          setEditData(detailData);
        }
        navigate(`/create-ad-categories?id=${adCategoryId}`);
      },
      showDivider: true,
    },
    {
      label: "Archive Ad Category",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () =>
        setCategoryToDelete({ id: adCategoryId, name: categoryName }),
    },
  ];

  const deletedMenuItem = (adCategoryId: string, categoryName: string) => [
    {
      label: `Re-activate ${categoryName}`,
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () =>
        setCategoryToRestore({ id: adCategoryId, name: categoryName }),
    },
  ];

  useEffect(() => {
    fetchData();
  }, [sortField, searchParams]);

  // Filter categories based on search
  const filteredCategories = Array.isArray(adCategoryApiData?.data)
    ? adCategoryApiData.data.filter((category) =>
        category.name.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  // Sort categories locally for frontend sorting
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case "name":
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case "ad_types":
        aValue = a.counts.ad_types;
        bValue = b.counts.ad_types;
        break;
      case "publishers":
        aValue = a.counts.publishers;
        bValue = b.counts.publishers;
        break;
      case "parameter_values":
        aValue = a.counts.parameter_values;
        bValue = b.counts.parameter_values;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Calculate pagination details
  const totalPages = Math.ceil(sortedCategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentCategories = sortedCategories.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const goPrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const goNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return (
      <svg
        className={`w-4 h-4 text-pink-500 transition-transform ${
          sortOrder === "desc" ? "rotate-180" : ""
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            {searchParams.get("type") === "deleted" ? "Archived" : null} Ad
            Categories Dashboard
          </h1>
          <button
            className="text-gray-500 hover:text-gray-700 underline text-sm"
            onClick={() => {
              if (searchParams.get("type") === "deleted") {
                searchParams.delete("type");
                setSearchParams(searchParams);
              } else {
                setSearchParams({
                  ...searchParams,
                  type: "deleted",
                });
              }
            }}
          >
            {searchParams.get("type") === "deleted"
              ? "Hide Archived Ad Categories"
              : "View Archived Ad Categories"}
          </button>
        </div>

        <div className="flex justify-between mb-6">
          <div>
            <TextField
              type="text"
              leadingIcon={<BiSearch />}
              placeholder="Search by Category name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-96"
            />
          </div>
          <div>
            <div>
              <Button
                trailingIcon={<FaPlus />}
                type="primary"
                onClick={() => {
                  resetValue();
                  navigate("/create-ad-categories");
                }}
              >
                Add New Category
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="pt-2">
              <table className="w-full mt-5">
                <thead className="border-b border-gray-300 text-gray-600/50 text-sm">
                  <tr>
                    <th className="text-left py-2">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("name")}
                      >
                        Category Name
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th className="text-left py-2">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("status")}
                      >
                        Status
                        <SortIcon field="status" />
                      </div>
                    </th>
                    <th className="text-left py-2">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("publishers")}
                      >
                        Publishers
                        <SortIcon field="publishers" />
                      </div>
                    </th>
                    <th className="text-left py-2">
                      <div
                        className="flex items-center cursor-pointer gap-1 select-none"
                        onClick={() => handleSort("ad_types")}
                      >
                        Ad Types
                        <SortIcon field="ad_types" />
                      </div>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {currentCategories.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-6 text-center text-gray-500"
                      >
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    currentCategories.map((adCategory, i) => (
                      <tr key={i} className="border-b border-gray-200">
                        <td className="py-4 font-semibold">
                          {adCategory.name}
                        </td>
                        <td className="py-4">
                          {searchParams.get("type") === "deleted"
                            ? "Archived"
                            : "Active"}
                        </td>
                        <td className="py-4">
                          {Array.isArray(adCategory.publishers) &&
                          adCategory.publishers.length > 0
                            ? adCategory.publishers
                                .map((publisher) => publisher?.name)
                                .join(", ")
                            : "-"}
                        </td>
                        <td className="py-4">
                          {Array.isArray(adCategory.ad_types) &&
                          adCategory.ad_types.length > 0
                            ? adCategory.ad_types
                                .map((adType) => adType?.name)
                                .join(", ")
                            : "-"}
                        </td>
                        <td>
                          <PopoverMenu
                            items={
                              searchParams.get("type")
                                ? deletedMenuItem(
                                    adCategory.ad_category_id,
                                    adCategory.name,
                                  )
                                : createMenuItems(
                                    adCategory.ad_category_id,
                                    adCategory.name,
                                  )
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
              <Button
                type="secondary"
                onClick={goPrevious}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span>
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                type="secondary"
                onClick={goNext}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={!!categoryToDelete}
        onOpenChange={() => setCategoryToDelete(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Archive Ad Category
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to archive{" "}
              <span className="font-semibold">{categoryToDelete?.name}</span>?
              This action can be undone by re-activating the category.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setCategoryToDelete(null)}>
              Cancel
            </Button>
            <Button
              type="primary"
              trailingIcon={<BiTrash fontSize={18} />}
              onClick={handleDelete}
            >
              Yes, Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-activate Confirmation Dialog */}
      <Dialog
        open={!!categoryToRestore}
        onOpenChange={() => setCategoryToRestore(null)}
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <div className="font-semibold text-xl text-center">
              Re-activate Ad Category
            </div>
            <DialogDescription className="mt-4 text-center text-gray-600">
              Are you sure you want to re-activate{" "}
              <span className="font-semibold">{categoryToRestore?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-4 mt-6">
            <Button type="secondary" onClick={() => setCategoryToRestore(null)}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleRestore}>
              Yes, Re-activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
